// Chi tiết một khách (ngăn kéo ở trang Khách hàng): thông tin + sửa, sổ địa chỉ giao hàng (thêm / sửa /
// xoá / đặt mặc định), lịch sử đơn (phân trang, bấm mở đơn), "Tạo đơn cho khách này".

import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { MapPin, Pencil, Phone, Plus, Star, Trash2 } from "lucide-react"
import { addressLine } from "@shoptrack/common/customer"
import { del, get, patch, ApiError } from "@/lib/api"
import { formatDate, getPhoneValidationError, money, phoneDisplay } from "@/lib/format"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { PhoneInput } from "@/components/ui/phone-input"
import { Sheet } from "@/components/ui/sheet"
import { IndexBadge } from "@/components/ui/data-table"
import { StatusPill } from "@/components/ui/status"
import { confirm } from "@/components/ui/confirm"
import { CustomerBadges } from "./CustomerSuggest"
import { CustomerAddressDialog } from "./CustomerAddressDialog"
import { useUnitCan } from "@/lib/unitAccess"
import type { CustomerDetail, CustomerOrder, SavedAddress } from "./types"

const ORDERS_PAGE = 20

export function CustomerDetailSheet({ id, onClose, onOpenCustomer }: { id: string | null; onClose: () => void; onOpenCustomer: (id: string) => void }) {
  const qc = useQueryClient()
  const nav = useNavigate()
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): sửa / địa chỉ cần quản lý khách; lịch sử đơn cần xem đơn
  const { can } = useUnitCan()
  const canManage = can("customers.manage")
  const canOrders = can("orders.view")
  const q = useQuery({
    queryKey: ["customers", "detail", id],
    queryFn: () => get<{ customer: CustomerDetail }>(`/customers/${id}`),
    enabled: Boolean(id),
  })
  const c = q.data?.customer

  const orders = useInfiniteQuery({
    queryKey: ["customers", "orders", id],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      get<{ orders: CustomerOrder[]; nextCursor: string | null }>(
        `/customers/${id}/orders?limit=${ORDERS_PAGE}${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    enabled: Boolean(id) && canOrders,
  })
  const orderRows = orders.data?.pages.flatMap((p) => p.orders) ?? []

  // ─── Sửa thông tin ──────────────────────────────────────────────
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState("")
  const [phone, setPhone] = useState<string | null>(null)
  const [note, setNote] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [owner, setOwner] = useState<{ customerId: string; name: string } | null>(null)
  useEffect(() => {
    setEditing(false)
    setError(null)
    setOwner(null)
  }, [id])
  const startEdit = () => {
    if (!c) return
    setName(c.name)
    setPhone(c.phone)
    setNote(c.note ?? "")
    setError(null)
    setOwner(null)
    setEditing(true)
  }
  const saveInfo = useMutation({
    mutationFn: () => patch(`/customers/${id}`, { name: name.trim(), phone: phone || null, note: note.trim() || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["customers"] })
      toast.success("Đã lưu thông tin khách")
      setEditing(false)
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "customer.phone_exists") setOwner(e.details as { customerId: string; name: string })
      else setError((e as Error).message)
    },
  })

  // ─── Sổ địa chỉ ─────────────────────────────────────────────────
  const [addrDialog, setAddrDialog] = useState<{ open: boolean; address: SavedAddress | null }>({ open: false, address: null })
  const setDefault = useMutation({
    mutationFn: (a: SavedAddress) => patch(`/customers/${id}/addresses/${a.id}`, { isDefault: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["customers"] }),
    onError: (e) => toast.error((e as Error).message),
  })
  const removeAddr = useMutation({
    mutationFn: (a: SavedAddress) => del(`/customers/${id}/addresses/${a.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["customers"] })
      toast.success("Đã xóa địa chỉ")
    },
    onError: (e) => toast.error((e as Error).message),
  })

  return (
    <Sheet
      open={Boolean(id)}
      onOpenChange={(v) => !v && onClose()}
      title={c?.name ?? "Khách hàng"}
      description={c ? <CustomerBadges c={c} /> : undefined}
    >
      {!c ? (
        <p className="py-6 text-center text-sm text-muted">{q.isError ? "Không tải được khách hàng." : "Đang tải…"}</p>
      ) : (
        <div className="space-y-6">
          {/* Thông tin */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-ink">Thông tin</h3>
              {!editing && canManage && (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={startEdit}>
                  <Pencil className="size-3.5" /> Sửa
                </Button>
              )}
            </div>
            {editing ? (
              <div className="space-y-3 rounded-lg border border-rule p-3">
                <label className="block space-y-1.5">
                  <span className="text-sm text-inkSoft">Tên khách *</span>
                  <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 desktop:h-9" disabled={saveInfo.isPending} />
                </label>
                <div className="space-y-1.5">
                  <span className="block text-sm text-inkSoft">Số điện thoại</span>
                  <PhoneInput value={phone} onValueChange={(v) => { setPhone(v); setOwner(null) }} showValidation disabled={saveInfo.isPending} />
                </div>
                <label className="block space-y-1.5">
                  <span className="text-sm text-inkSoft">Ghi chú</span>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} disabled={saveInfo.isPending} />
                </label>
                {owner && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">
                    <span>Số điện thoại này đã thuộc khách <b>{owner.name}</b>.</span>
                    <Button type="button" size="sm" variant="outline" className="ml-auto h-10 desktop:h-8" onClick={() => onOpenCustomer(owner.customerId)}>
                      Mở khách này
                    </Button>
                  </div>
                )}
                {error && <p className="text-sm text-danger">{error}</p>}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" className="h-11 desktop:h-9" disabled={saveInfo.isPending} onClick={() => setEditing(false)}>
                    Hủy
                  </Button>
                  <Button
                    className="h-11 desktop:h-9"
                    disabled={saveInfo.isPending}
                    onClick={() => {
                      setError(null)
                      if (!name.trim()) return setError("Nhập tên khách")
                      const err = getPhoneValidationError(phone, false)
                      if (err) return setError(err)
                      saveInfo.mutate()
                    }}
                  >
                    {saveInfo.isPending ? "Đang lưu…" : "Lưu"}
                  </Button>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-[7rem_1fr] gap-y-1.5 text-sm">
                <dt className="text-muted">Điện thoại</dt>
                <dd>
                  {c.phone ? (
                    <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 tabular-nums text-ink hover:text-accent">
                      <Phone className="size-3.5" /> {phoneDisplay(c.phone)}
                    </a>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </dd>
                <dt className="text-muted">Số đơn</dt>
                <dd className="text-ink">{c.orderCount}{c.lastOrderAt && <span className="text-muted"> · gần nhất {formatDate(c.lastOrderAt)}</span>}</dd>
                <dt className="text-muted">Boom</dt>
                <dd className={c.boomCount > 0 ? "font-medium text-danger" : "text-muted"}>{c.boomCount}</dd>
                <dt className="text-muted">Ghi chú</dt>
                <dd className="whitespace-pre-line text-ink">{c.note || <span className="text-muted">—</span>}</dd>
                <dt className="text-muted">Ngày thêm</dt>
                <dd className="text-muted">{formatDate(c.createdAt)}</dd>
              </dl>
            )}
          </section>

          {/* Sổ địa chỉ */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-ink">Địa chỉ giao hàng ({c.addresses.length})</h3>
              {canManage && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAddrDialog({ open: true, address: null })}>
                <Plus className="size-3.5" /> Thêm địa chỉ
              </Button>}
            </div>
            {c.addresses.length === 0 ? (
              <p className="rounded-lg bg-paperSoft px-3 py-2 text-sm text-muted">Chưa có địa chỉ.</p>
            ) : (
              <ul className="space-y-2">
                {c.addresses.map((a) => (
                  <li key={a.id} className="rounded-lg border border-rule p-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="mt-0.5 size-4 shrink-0 text-muted" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-ink">
                          {addressLine(a)}
                          {a.isDefault && <span className="ml-1.5 rounded bg-accent/12 px-1.5 py-0.5 text-xs font-medium text-accent">Mặc định</span>}
                          {a.label && <span className="ml-1.5 text-xs font-medium text-inkSoft">{a.label}</span>}
                        </p>
                        <p className="text-xs text-muted">
                          {a.useCount > 0 ? `Đã giao ${a.useCount} lần${a.lastUsedAt ? ` · lần cuối ${formatDate(a.lastUsedAt)}` : ""}` : "Chưa giao lần nào"}
                          {!a.city && " · chưa có tỉnh / phường"}
                        </p>
                      </div>
                    </div>
                    {canManage && <div className="mt-2 flex flex-wrap justify-end gap-1.5">
                      {!a.isDefault && (
                        <Button size="sm" variant="ghost" className="gap-1 text-xs" disabled={setDefault.isPending} onClick={() => setDefault.mutate(a)}>
                          <Star className="size-3.5" /> Đặt mặc định
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="gap-1 text-xs" onClick={() => setAddrDialog({ open: true, address: a })}>
                        <Pencil className="size-3.5" /> Sửa
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1 text-xs text-danger hover:bg-danger/10"
                        disabled={removeAddr.isPending}
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Xóa địa chỉ này?",
                            description: `${addressLine(a)}. Đơn cũ không đổi.`,
                            confirmLabel: "Xóa",
                            tone: "danger",
                          })
                          if (ok) removeAddr.mutate(a)
                        }}
                      >
                        <Trash2 className="size-3.5" /> Xóa
                      </Button>
                    </div>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Lịch sử đơn */}
          {canOrders && <section className="space-y-2">
            <h3 className="text-sm font-semibold text-ink">Lịch sử đơn ({c.orderCount})</h3>
            {orderRows.length === 0 ? (
              <p className="rounded-lg bg-paperSoft px-3 py-2 text-sm text-muted">{orders.isLoading ? "Đang tải…" : "Chưa có đơn"}</p>
            ) : (
              <ul className="divide-y divide-rule rounded-lg border border-rule">
                {orderRows.map((o, i) => (
                  <li key={o.id}>
                    <Link to={`/orders/${encodeURIComponent(o.id)}`} className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm hover:bg-paperSoft">
                      <IndexBadge n={i + 1} />
                      <span className="text-xs text-muted tabular-nums">{formatDate(o.createdAt)}</span>
                      <span className="font-mono text-xs text-ink">{o.clientOrderId || o.id}</span>
                      <StatusPill status={o.status} />
                      <span className="ml-auto font-medium tabular-nums text-ink">{money(o.cod)}</span>
                      {o.recipientName && o.recipientName !== c.name && (
                        <span className="w-full text-xs text-muted">Người nhận: {o.recipientName}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {orders.hasNextPage && (
              <Button variant="outline" className="w-full" onClick={() => orders.fetchNextPage()} disabled={orders.isFetchingNextPage}>
                {orders.isFetchingNextPage ? "Đang tải…" : "Xem thêm đơn"}
              </Button>
            )}
          </section>}

          {can("orders.create") && (
            <Button className="w-full h-11" onClick={() => nav(`/orders/new?customerId=${encodeURIComponent(c.id)}`)}>
              Tạo đơn
            </Button>
          )}

          <CustomerAddressDialog
            customerId={c.id}
            address={addrDialog.address}
            open={addrDialog.open}
            onOpenChange={(open) => setAddrDialog((s) => ({ ...s, open }))}
          />
        </div>
      )}
    </Sheet>
  )
}
